import { selectCurrentProperty } from "@store/property/propertySelector";
import { FC, ReactNode, useEffect, useRef } from "react";
import useAppSelector from "@hooks/useAppSelector";
import { selectCurrentUserId } from "@store/user/userSelectors";
import { Property } from "@store/property/types";
import { fetchSpace, fetchSpaces } from "@store/space/spaceThunk";
import useAppDispatch from "@hooks/useAppDispatch";
import { fetchProperties } from "@store/property/propertyThunk";
import { removeSpace } from "@store/space/spaceSlice";
import { removeIssue } from "@store/issue/issueSlice";
import { fetchDayReport } from "@store/dayReport/dayReportThunk";
import { fetchIssue } from "@store/issue/issueThunk";
import { API_ROOT } from "@services/api";
interface PropertyProviderProps {
  children: ReactNode;
}

const PUBLIC_API = API_ROOT.replace(/http.?:\/\//, "");

const WS_MAX_RECONNECT_RETRIES = 10;
const WS_STALE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

const PropertyProvider: FC<PropertyProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const selectedProperty = useAppSelector(selectCurrentProperty);
  const userPropertyIds = useAppSelector(
    ({ user }) => user.current?.properties,
  );
  const currentUserId = useAppSelector(selectCurrentUserId);
  const subscribedProperty = useRef<Property | undefined>();
  const ws = useRef<WebSocket | null>(null);
  const isMounted = useRef(true);
  const retryCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageTime = useRef(Date.now());

  const code = selectedProperty?.code;

  const messageHandler = (event: MessageEvent) => {
    lastMessageTime.current = Date.now();

    const payload = JSON.parse(event.data);
    // Ignore ping messages and other types with no relevant updates
    if (!payload.message || !payload.message.entity) return;

    const { entity, id, initiator_id, action } = payload.message as {
      entity: "day_report" | "issue" | "space";
      action: "create" | "update" | "destroy";
      id: string;
      initiator_id?: string; // $platform-$userId
    };

    let actionToDispatch = null;
    const fetch = action !== "destroy";
    // Ignore self-initiated broadcasts (except for spaces, to reconcile
    // fields not covered by optimistic updates like vacantSince)
    if (entity !== "space" && initiator_id === `web-${currentUserId}`) return;

    switch (entity) {
      case "day_report":
        // We don't destroy day reports
        if (fetch) actionToDispatch = fetchDayReport(id);
        break;
      case "issue":
        actionToDispatch = fetch ? fetchIssue(id) : removeIssue(id);
        break;
      case "space":
        actionToDispatch = fetch ? fetchSpace(id) : removeSpace(id);
        break;
    }
    if (actionToDispatch !== null) dispatch(actionToDispatch);
  };

  const subscribeToProperty = (selectedProperty: Property) => {
    if (subscribedProperty.current) {
      ws.current?.send(
        JSON.stringify({
          command: "unsubscribe",
          identifier: JSON.stringify(subscribedProperty.current?.stream),
        }),
      );
    }
    ws.current?.send(
      JSON.stringify({
        command: "subscribe",
        identifier: JSON.stringify(selectedProperty.stream),
      }),
    );
  };

  // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ... (capped at 30s)
  // Gives up after WS_MAX_RECONNECT_RETRIES attempts
  const scheduleReconnect = () => {
    if (!isMounted.current) return;
    if (retryCount.current >= WS_MAX_RECONNECT_RETRIES) {
      console.log(
        `+++ WebSocket reconnection failed after ${WS_MAX_RECONNECT_RETRIES} attempts`,
      );
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
    retryCount.current += 1;
    console.log(
      `+++ scheduling WebSocket reconnect (attempt ${retryCount.current}) in ${delay}ms`,
    );

    reconnectTimer.current = setTimeout(() => {
      if (!isMounted.current) return;
      connectWebSocket();
    }, delay);
  };

  const connectWebSocket = () => {
    console.log("+++ connecting to socket");
    if (ws.current) {
      ws.current.onclose = null; // prevent recursive reconnect from this close
      ws.current.close();
    }
    ws.current = new WebSocket(`wss://${PUBLIC_API}/stream`);

    ws.current.onopen = () => {
      console.log("+++ socket connected");
      retryCount.current = 0;

      if (subscribedProperty.current) {
        ws.current?.send(
          JSON.stringify({
            command: "subscribe",
            identifier: JSON.stringify(subscribedProperty.current.stream),
          }),
        );
        // Reconcile any updates missed while disconnected
        dispatch(fetchSpaces());
      }
    };

    ws.current.onmessage = messageHandler;

    ws.current.onclose = () => {
      ws.current = null;
      scheduleReconnect();
    };

    ws.current.onerror = () => {
      // Browser fires onclose after onerror; reconnect is handled there
    };
  };

  const startWebSocket = (selectedProperty: Property) => {
    if (!ws.current) {
      connectWebSocket();
      return;
    }
    subscribeToProperty(selectedProperty);
  };

  // Refresh spaces when tab becomes visible after prolonged inactivity
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastMessageTime.current > WS_STALE_THRESHOLD_MS
      ) {
        dispatch(fetchSpaces());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!code || !selectedProperty) return;
    startWebSocket(selectedProperty);
    subscribedProperty.current = selectedProperty;
  }, [code]);

  useEffect(() => {
    if (!userPropertyIds?.length) return;

    dispatch(fetchProperties());
  }, [userPropertyIds]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      ws.current?.close();
    };
  }, []);

  return <>{children}</>;
};

export default PropertyProvider;
